import { createSelector } from 'reselect';
import { format } from 'date-fns';
import { toCurrency } from '@/utilities/formatting';
import { get } from 'lodash';
import {
  getFormatting,
  getInsiderTransactionsKeys,
  getInsiderTransactionsMap,
} from '@/pages/CompanyReport/redux/selectors';
import { getPeople } from '@ducks/people/selectors';
export interface InsiderTransation {
  filingDate: string;
  transactionType: string;
  transactionValue: string;
  ownerName: string;
  ownerType: string;
  teamTitle: string;
  boardTitle: string;
  shares: string;
  priceMax: string;
}
const transactionSorter =
  (transactionsMap: ReturnType<typeof getInsiderTransactionsMap>) => (a, b) => {
    const { filingDate: filingDateA } = transactionsMap[a];
    const { filingDate: filingDateB } = transactionsMap[b];
    return (filingDateB || 0) - (filingDateA || 0);
  };

const transactionMapper =
  (
    transactionsMap: ReturnType<typeof getInsiderTransactionsMap>,
    peopleMap: ReturnType<typeof getPeople>,
    formatting: ReturnType<typeof getFormatting>
  ) =>
  (transaction) => {
    const currentTransaction = transactionsMap[transaction];
    const person = peopleMap[currentTransaction.ownerObjectId];
    return {
      filingDate: format(currentTransaction.filingDate || 0, 'dd MMM yy'),
      transactionType: currentTransaction.transactionType,
      transactionValue: toCurrency(
        Math.abs(currentTransaction.transactionValue),
        0,
        formatting.tradingItemCurrencyISO
      ),
      ownerName: currentTransaction.ownerName,
      ownerType: currentTransaction.ownerType,
      teamTitle: get(person, 'proTitle', ''),
      boardTitle: get(person, 'shortTitle', ''),
      shares: Math.abs(currentTransaction.shares).toLocaleString(),
      priceMax: toCurrency(
        currentTransaction.priceMax,
        2,
        formatting.tradingItemCurrencyISO
      ),
    };
  };

export const makeInsiderTradingTransactions = () =>
  createSelector(
    [
      getFormatting,
      getInsiderTransactionsKeys,
      getInsiderTransactionsMap,
      getPeople,
    ],
    (formatting, transactionsKeys, transactionsMap, peopleMap) => {
      return transactionsKeys
        .filter((n) => transactionsMap[n].filingDate !== null)
        .sort(transactionSorter(transactionsMap))
        .map<InsiderTransation>(
          transactionMapper(transactionsMap, peopleMap, formatting)
        );
    }
  );
